import { useCallback, useEffect, useState } from 'react';

import { getCase, getCaseImage, postCaseInit } from '@/api/cases';
import { ENDPOINTS } from '@/api/endpoints';
import { errorlog } from '@/libs/error-logs';
import { CASE_STATUS } from '@/utils/cases';

const isCaseProcessing = (status?: number) => {
  if (!status) return false;
  return status === CASE_STATUS.IN_PROGRESS || status === CASE_STATUS.PENDING;
};

const isCaseNotStarted = (status?: number) => {
  if (!status) return false;
  return status === CASE_STATUS.NOT_STARTED || status === CASE_STATUS.NOT_SUBMITTED;
};

type UseCaseProcessingParams = {
  caseId: string;
  isNewCase?: boolean;
};

type UseCaseProcessingReturn = {
  imageList: File[];
  caseResult: number | null;
  isLoadingImages: boolean;
  sendingToProcessing: boolean;
  handleFileDelete: (fileName: string) => void;
  handleUploadSuccess: (file: File) => void;
  initProcess: (caseType?: string, quality?: number) => Promise<void>;
};

export function useCaseProcessing(params: UseCaseProcessingParams): UseCaseProcessingReturn {
  const { caseId, isNewCase } = params;

  const [imageList, setImageList] = useState<File[]>([]);
  const [caseResult, setCaseResult] = useState<number | null>(null);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [sendingToProcessing, setSendingToProcessing] = useState(false);

  const processingProgress = useCallback(async () => {
    try {
      if (!caseId) return;

      setSendingToProcessing(true);

      const results = await getCase(caseId, 'score');
      const result = parseInt(Array.isArray(results) ? results[0] : results, 10);

      setCaseResult(result);

      if (isCaseProcessing(result)) {
        setTimeout(() => {
          void processingProgress();
        }, 15000);
      } else {
        setSendingToProcessing(false);
      }
    } catch (error) {
      errorlog(error as Error, 'src/hooks/useCaseProcessing', 'Error processing case');
    }
  }, [caseId]);

  const handleFileDelete = useCallback((fileName: string) => {
    setImageList((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  const handleUploadSuccess = useCallback((file: File) => {
    setImageList((prev) => [...prev, file]);
  }, []);

  const initProcess = useCallback(
    // case type can be set by the image types, but at this moment it is set as 'auto'
    async (caseType = 'auto', quality = 1) => {
      try {
        setSendingToProcessing(true);
        await postCaseInit(caseId, caseType, String(quality));

        // trigger the processing progress validation
        void processingProgress();
      } catch (error) {
        errorlog(error as Error, 'src/hooks/useCaseProcessing', 'Error processing case');
      }
    },
    [caseId, processingProgress]
  );

  useEffect(() => {
    // if the case is new, we don't need to load the images or check the status
    if (isNewCase) return;

    let isMounted = true;

    // get the default images, just in case we were returning to this screen
    void (async () => {
      setIsLoadingImages(true);
      const _case = await getCase(caseId, 'list');

      if (!isMounted) return;

      if (typeof _case === 'string' || !Array.isArray(_case)) {
        setIsLoadingImages(false);
        setImageList([]);
        return;
      }

      const caseImgs = _case?.map((img) =>
        ENDPOINTS.CASES_IMAGE.replace('{caseId}', caseId).replace('{imgPath}', img)
      );

      const urls = caseImgs.filter((url) => !url.includes('.zip'));
      const promises = await Promise.all(urls.map(getCaseImage));

      if (!isMounted) return;

      if (promises) {
        const images = promises.filter((response) => response.file instanceof Blob);
        if (images.length) {
          setImageList(images.map((i) => i.file));
        }
      }

      // check if the case is already in progress
      const results = await getCase(caseId, 'score');
      const result = parseInt(Array.isArray(results) ? results[0] : results, 10);

      if (!isMounted) return;

      if (isCaseNotStarted(result)) {
        setIsLoadingImages(false);
        return;
      }

      setCaseResult(result);
      setTimeout(() => {
        void processingProgress();
      }, 5000);

      setIsLoadingImages(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [caseId, isNewCase, processingProgress]);

  return {
    imageList,
    caseResult,
    isLoadingImages,
    sendingToProcessing,
    handleFileDelete,
    handleUploadSuccess,
    initProcess,
  };
}
